import { useEffect, useState } from 'react'

/** 키보드가 가려서 비워야 하는 높이와, 그만큼 움직이는 데 걸리는 시간 */
export interface KeyboardInset {
  padding: number
  /** 이 시간 동안 움직여야 키보드와 같이 간다 (ms). 0이면 즉시 */
  duration: number
}

type Bridge = {
  keyboardReporting?: boolean
  keyboardHeight?: number
  keyboardDuration?: number
}

const bridge = (): Bridge =>
  (window as unknown as { FlutterBridge?: Bridge }).FlutterBridge ?? {}

/**
 * 키보드가 가린 만큼 화면 아래를 비운다. 키보드와 같은 속도로 움직인다.
 *
 * 두 가지 정보가 필요하다 — 실기기 측정으로 확인
 * -------------------------------------------
 * ① **얼마나** 비울지. 앱이 알려주는 높이를 쓴다.
 * ② **언제** 비울지. 앱이 알려주는 애니메이션 길이만큼 나눠서 움직인다.
 *
 * 보이는 높이(visualViewport)로 재면 ①은 정확하지만 ②가 안 된다. 갤럭시에서
 * 그 값은 키보드 애니메이션이 **끝난 뒤** 한 번에 바뀐다:
 *
 *     190ms  vv880  bar874  pad0
 *     476ms  vv508  bar502  pad372   ← 중간값 없이 여기서 한 번에
 *
 * 그래서 키보드가 다 올라온 다음 입력칸이 뒤늦게 따라왔다.
 *
 * 반대로 알려준 높이만 쓰고 즉시 적용하면 ②가 반대로 어긋난다. 키보드가
 * 아직 덮지도 않았는데 입력칸만 먼저 올라가 한 번 튄다.
 *
 * 그래서 알려준 높이를 목표로 삼되, 알려준 길이에 맞춰 움직인다. 쓰는 쪽에서
 * 이 duration을 CSS transition에 넣으면 키보드와 같은 시간 동안 이동한다.
 *
 * 알려주지 않는 빌드에서는
 * ----------------------
 * 보이는 높이로 계산한다. 늦게 따라오지만 위치는 맞다. 옛 앱에서도 입력칸이
 * 키보드에 가리지는 않는다.
 */
export function useKeyboardInset(): KeyboardInset {
  const [padding, setPadding] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const supported = bridge().keyboardReporting === true
    const viewport = window.visualViewport

    const onKeyboard = (e: Event) => {
      const detail = (e as CustomEvent<{ height: number; duration: number }>)
        .detail
      if (!detail) return

      const target = Math.max(0, Math.round(detail.height))
      const full = Math.max(0, Math.round(detail.duration))

      /*
       * 올라올 때는 **즉시** 자리를 잡는다. 따라가며 움직이지 않는다.
       *
       * 왜 — 실기기 측정(아이폰, 2026-10-30, 자세히보기 답글)
       * ----------------------------------------------
       * 알려준 길이(250ms)만큼 같이 움직이게 했더니, 입력칸이 키보드를 한참
       * 뒤에서 쫓아 올라왔다. 그 기어오름이 '출렁'의 정체였다.
       *
       *   t 88 top652   ← 알림 도착. 키보드는 이미 vv494
       *   t155 top455
       *   t289 top417   ← 200ms 뒤에야 자리를 잡는다
       *
       * 늦은 만큼 깎아 보려고 visualViewport 로 진행률을 재 봤지만 안 됐다.
       * iOS 에서 그 값은 키보드를 따라 조금씩 변하지 않고 한 번에 튄다 —
       * 진행률이 늘 0 아니면 1이라 깎을 수가 없다.
       *
       * 그래서 아예 안 움직인다. 자리를 먼저 잡아 두면 키보드가 그 위를 덮으며
       * 올라올 뿐이라, 따로 노는 것이 보이지 않는다. 내려갈 때는 알려준 길이를
       * 그대로 쓴다 — 그쪽은 키보드가 빠지며 드러나는 것이라 뚝 끊기면 어색하다.
       */
      lastTargetRef = target
      setDuration(target > 0 ? 0 : full)
      setPadding(effective(target))
    }

    /*
     * 창이 이미 줄어든 만큼은 빼고 비운다.
     *
     * 왜 — 실기기 측정(아이폰, 2026-10-30)
     * ----------------------------------
     * 웹뷰는 키보드에 맞춰 줄지 않기로 돼 있는데(resize=false), 실제로는 한 프레임
     * 줄었다 돌아온다. 그 프레임에 우리 여백 380 까지 들어가 있으면 이중으로
     * 올라가, 입력칸이 화면 꼭대기까지 튀었다가 돌아온다.
     *
     *   t 78  ih874 top417   ← 자리 잡음
     *   t 98  ih494 top37    ← 창이 380 줄었는데 여백도 380 → 760 만큼 올라감
     *   t 98  ih874 top417   ← 한 프레임 뒤 복구
     *
     * 창이 줄어든 만큼은 키보드를 이미 피한 것이다. 그만큼 빼면 그 프레임에도
     * 입력칸이 제자리에 있다. 창이 안 줄면(정상) 빼는 값이 0이라 그대로다.
     *
     * 이 보정은 웹뷰를 줄이는 옛 앱에서도 맞게 동작한다 — 거기서는 늘 창이
     * 줄어들므로 여백이 0이 되고, 레이아웃은 줄어든 창에 맞춰 알아서 선다.
     */
    function effective(target: number): number {
      const shrunk = Math.max(0, baseH - window.innerHeight)
      return Math.max(0, target - shrunk)
    }

    /** 키보드가 없을 때의 창 높이. 그보다 줄어든 만큼이 '창이 이미 피한 양'이다. */
    let baseH = window.innerHeight
    let lastTargetRef = 0

    const onWindowResize = () => {
      if (window.innerHeight > baseH) baseH = window.innerHeight
      setPadding(effective(lastTargetRef))
    }

    // 알려주지 않는 빌드: 보이는 높이로 잰다
    const fromViewport = () => {
      if (!viewport) return
      const hidden = window.innerHeight - (viewport.height + viewport.offsetTop)
      setPadding(hidden > 1 ? Math.round(hidden) : 0)
      setDuration(0)
    }

    if (supported) {
      /*
       * 키보드가 이미 떠 있는 동안 처음 그려지는 화면도 있다(입력 중에 나타나는
       * 빈 결과 문구 등). 이벤트는 이미 지나갔으므로 앱이 남겨 둔 마지막 값에서
       * 시작한다. 키보드가 내려가면 앱이 0을 보내 두므로 오래된 값이 남지 않는다.
       */
      setPadding(Math.max(0, Math.round(bridge().keyboardHeight ?? 0)))
      window.addEventListener('tastee:keyboard', onKeyboard)
      window.addEventListener('resize', onWindowResize)
      return () => {
        window.removeEventListener('tastee:keyboard', onKeyboard)
        window.removeEventListener('resize', onWindowResize)
      }
    }

    fromViewport()
    viewport?.addEventListener('resize', fromViewport)
    viewport?.addEventListener('scroll', fromViewport)
    window.addEventListener('resize', fromViewport)
    return () => {
      viewport?.removeEventListener('resize', fromViewport)
      viewport?.removeEventListener('scroll', fromViewport)
      window.removeEventListener('resize', fromViewport)
    }
  }, [])

  return { padding, duration }
}
